import { CheckCircleFilled } from '@ori-ui/icons';
import { Typography, styled, Button } from '@ori-ui/mui';
import { useGetTenantDigitalCatalogueDynamiclink } from '@ori/static-api-client-react';
import useTestId from '@ori/testid-generator';
import type { FC } from 'react';
import { useState, useEffect } from 'react';

import { Localization } from '../../Localization';
import { defaultTranslations } from '../../constants/defaultTranslations_simplified-registration-BrandPartner';
import { useConfiguration, useVirtualPageAnalyticsData } from '../../contexts';
import { TranslationKey } from '../../models';
import { virtualPageEventDispatch, useMobileOperatingSystem } from '../../utils';

const StyledWelcomeTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  marginTop: theme.spacing(6),
  fontWeight: 'bold',
}));

const WelcomeWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  height: '100%',
  marginTop: theme.spacing(4),
}));

const WelcomeActionButton = styled(Button)(({ theme }) => ({
  minWidth: '330px',
  marginTop: theme.spacing(8),
}));

const WelcomeAppButton = styled(Button)(({ theme }) => ({
  minWidth: '330px',
  marginTop: theme.spacing(6),
  backgroundColor: '#F0F9F6',
  color: theme.palette.primary.dark,
}));

const VerificationIconSuccess = styled(CheckCircleFilled)(({ theme }) => ({
  fontSize: '60px',
  color: theme.palette.primary.main,
}));

export const WelcomeStep: FC = () => {
  const { customerDashboardUrl, registrationId, tenant } = useConfiguration();
  const [isRedirectDisabled, setIsRedirectDisabled] = useState(false);
  const { getTestIdProps } = useTestId();
  const { isMobileOperatingSystem } = useMobileOperatingSystem();
  const {
    virtualPageAnalyticsContext: {
      virtualPageAnalyticsData: { previousStep },
    },
  } = useVirtualPageAnalyticsData();
  const { data: dynamicLink } = useGetTenantDigitalCatalogueDynamiclink(tenant, {
    fallbackToWeb: false,
    clientApplication: 'mv7',
  });

  const handleRedirectToDashboard = () => {
    if (customerDashboardUrl) {
      setIsRedirectDisabled(true);
      window.location.href = customerDashboardUrl;
    }
  };

  const handleRedirectToApp = () => {
    if (dynamicLink?.link) {
      setIsRedirectDisabled(true);
      window.location.href = dynamicLink.link;
    }
  };

  useEffect(() => {
    virtualPageEventDispatch('SubmitStep', previousStep, registrationId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- virtual page event should be fired exacly once, no need to reevaluate
  }, []);

  useEffect(() => {
    // Reset disabled buttons on back navigation for mobile devices
    const handleLocationChange = () => {
      setIsRedirectDisabled(false);
    };
    window.addEventListener('visibilitychange', handleLocationChange);

    return () => window.removeEventListener('visibilitychange', handleLocationChange);
  }, []);

  return (
    <WelcomeWrapper>
      <VerificationIconSuccess />
      <StyledWelcomeTitle
        variant="h2"
        color="textPrimary"
      >
        <Localization
          id={TranslationKey.Welcome_Header}
          defaultMessage={defaultTranslations.Welcome_Header}
        />
      </StyledWelcomeTitle>
      <Typography
        variant="subtitle1"
        color="textPrimary"
        textAlign="center"
      >
        <Localization
          id={TranslationKey.Welcome_Text}
          defaultMessage={defaultTranslations.Welcome_Text}
        />
      </Typography>
      <WelcomeActionButton
        variant="contained"
        color="primary"
        disabled={isRedirectDisabled}
        onClick={() => handleRedirectToDashboard()}
        {...getTestIdProps('welcome-step-btn-welcome')}
      >
        <Localization
          id={TranslationKey.Welcome_Button}
          defaultMessage={defaultTranslations.Welcome_Button}
        />
      </WelcomeActionButton>
      {isMobileOperatingSystem ? (
        <WelcomeAppButton
          variant="contained"
          color="primary"
          disabled={isRedirectDisabled}
          onClick={() => handleRedirectToApp()}
          {...getTestIdProps('welcome-step-btn-welcome')}
        >
          <Localization
            id={TranslationKey.Welcome_App}
            defaultMessage={defaultTranslations.Welcome_App}
          />
        </WelcomeAppButton>
      ) : null}
    </WelcomeWrapper>
  );
};
